import FormSymbolImages from "./AddSymbolPrintList";
import CONFIG from "config/config";
import CompanyLogoForm from "react-lib/apps/HISV3/common/CompanyLogoForm";
import {
  calculateHeaderBasedMargins,
  getAddressCompany,
  truncateTextSimple,
} from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

export type HeaderPrintListProps = Partial<{
  createdDate: string;
  patientInfo: any;
  detail: any;
  formName: string;
  styles: Record<string, any>;
  header: any;
  footer: any;
  images: any;
  pageMargins: number[];
  headerMargins: number[];
  canvasHeight: number;
  // optional for static pdf
  freeLine: number; // -- เพิ่มจำนวนบรรทัด free
  extraCanvasHeight: number; // -- เพิ่มความสูงให้ canvas สำหรับปิดพื้นหลังเพิ่มเติม
}>;

/*                            Note                                */
// HeaderPrintList [TH/EN] => แสดงข้อมูลของผู้ป่วย (ปัจจุบันเป็น Sapiens)
// HeaderSapiensInfo => แสดงข้อมูลของโรงพยาบาล Sapiens

const FORM_NAME = "AddHeaderSapiensPrintList";
const DEFAULT_PAGE_MARGINS = [38, 120, 38, 40];

const LOGO_HEIGHT = 30;
const LOGO_RESIZE =
  1 -
  (CONFIG.LOGO_REPORT.find((i: any) => i.type === 2)?.height - LOGO_HEIGHT) /
    CONFIG.LOGO_REPORT.find((i: any) => i.type === 2)?.height;
const LOGO_WIDTH = CONFIG.LOGO_REPORT.find((i: any) => i.type === 2)?.width * LOGO_RESIZE;

export const AddHeaderSapiensPrintListTH = async (doc: any, props: HeaderPrintListProps) => {
  const companyLogoForm = await CompanyLogoForm({
    type: 2,
    font: "KanitLM",
    height: LOGO_HEIGHT,
    form: FORM_NAME,
  });
  const { images } = companyLogoForm;

  return {
    ...doc,
    // logo height 80 + marginHeader 16 = 96 (pageMargins must greater than Header + Header Bottom Margin)
    // margin: [left, top, right, bottom]
    pageMargins: props.pageMargins ? props.pageMargins : DEFAULT_PAGE_MARGINS,
    header: () => {
      return {
        table: {
          widths: ["55%", "45%"],
          body: [
            [
              // Left Side (logo and form name)
              {
                stack: [
                  {
                    image: "logo",
                    width: LOGO_WIDTH,
                    height: LOGO_HEIGHT,
                    margin: [0, 0, 0, 24],
                  },
                  {
                    text: props.formName,
                    alignment: `center`,
                    font: `KanitLM`,
                    fontSize: 10,
                    bold: true,
                    margin: [0, -16, 0, 0],
                  },
                ],
                border: [true, true, false, true],
              },
              // Right Side (patient information)
              {
                stack: [
                  {
                    text: ` `,
                    preserveLeadingSpaces: true,
                    font: `KanitLM`,
                    fontSize: 8,
                  },
                  {
                    text: `ชื่อ : ${props.patientInfo?.patientFullName}`,
                    font: `KanitLM`,
                    fontSize: 8,
                    bold: true,
                  },
                  {
                    columns: [
                      {
                        text: `HN : ${props.patientInfo?.hn}`,
                        font: `KanitLM`,
                        fontSize: 8,
                        bold: true,
                      },
                      {
                        text: `EN/AN : ${props.patientInfo?.patientEncounter}`,
                        font: `KanitLM`,
                        fontSize: 8,
                        bold: true,
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: `วันเกิด : ${props.patientInfo?.birthdate}`,
                        font: `KanitLM`,
                        fontSize: 8,
                        bold: true,
                      },
                      {
                        text: `อายุ : ${props.patientInfo?.ageYear} ปี ${props.patientInfo?.ageMonth} เดือน ${props.patientInfo?.ageDay} วัน`,
                        font: `KanitLM`,
                        fontSize: 8,
                        bold: true,
                      },
                    ],
                  },
                  {
                    text: `เพศ : ${
                      props.patientInfo?.gender
                        ? props.patientInfo.gender === "F"
                          ? "หญิง"
                          : props.patientInfo.gender === "M"
                          ? "ชาย"
                          : props.patientInfo.gender
                        : "ไม่ระบุ"
                    }`,
                    font: `KanitLM`,
                    fontSize: 8,
                    bold: true,
                  },
                  {
                    text: ` `,
                    preserveLeadingSpaces: true,
                    font: `KanitLM`,
                    fontSize: 8,
                  },
                ],
                border: [false, true, true, true],
              },
            ],
          ],
        },
        layout: {
          paddingTop: function (i: any, node: any) {
            return 5;
          },
          paddingBottom: function (i: any, node: any) {
            return 5;
          },
          paddingLeft: function (i: any, node: any) {
            return 5;
          },
          paddingRight: function (i: any, node: any) {
            return 5;
          },
        },
        margin: [16, 16, 16, 0],
      };
    },
    footer: (currentPage: any, pageCount: any) => {
      if (props.footer) {
        return {
          columns: [
            {
              text: `วันที่พิมพ์ : ${props.createdDate}`,
              alignment: `left`,
              font: `KanitLM`,
              fontSize: 8,
            },
            {
              text: props.footer,
              alignment: `center`,
              font: `THSarabunNew`,
              fontSize: 12,
            },
            {
              text: `หน้า ` + currentPage.toString() + ` จาก ` + pageCount,
              alignment: `right`,
              font: `KanitLM`,
              fontSize: 8,
            },
          ],
          margin: [16, 16, 16, 16],
        };
      } else {
        return {
          columns: [
            {
              text: `วันที่พิมพ์ : ${props.createdDate}`,
              alignment: `left`,
              font: `KanitLM`,
              fontSize: 8,
            },
            {
              text: `หน้า ` + currentPage.toString() + ` จาก ` + pageCount,
              alignment: `right`,
              font: `KanitLM`,
              fontSize: 8,
            },
          ],
          margin: [16, 16, 16, 16],
        };
      }
    },
    images: {
      ...images,
      checked: FormSymbolImages.checked,
      unchecked: FormSymbolImages.unchecked,
      squarebox: FormSymbolImages.squarebox,
      checkmark: FormSymbolImages.checkmark,
      ...props.images,
    },
    styles: {
      ...(props.styles || {}),
    },
  };
};

export const AddHeaderSapiensPrintListEN = async (doc: any, props: HeaderPrintListProps) => {
  const companyLogoForm = await CompanyLogoForm({
    type: 2,
    font: "KanitLM",
    height: LOGO_HEIGHT,
    form: FORM_NAME,
  });
  const { images } = companyLogoForm;

  return {
    ...doc,
    // logo height 80 + marginHeader 16 = 96 (pageMargins must greater than Header + Header Bottom Margin)
    // margin: [left, top, right, bottom]
    pageMargins: props.pageMargins ? props.pageMargins : DEFAULT_PAGE_MARGINS,
    header: () => {
      return {
        table: {
          widths: ["52%", "48%"],
          body: [
            [
              // Left Side (logo and form name)
              {
                stack: [
                  {
                    image: "logo",
                    width: LOGO_WIDTH,
                    height: LOGO_HEIGHT,
                    margin: [0, 0, 0, 24],
                  },
                  {
                    text: props.formName,
                    alignment: `center`,
                    font: `Poppins`,
                    fontSize: 10,
                    bold: true,
                    margin: [0, -16, 0, 0],
                  },
                ],
                border: [true, true, false, true],
              },
              // Right Side (patient information)
              {
                stack: [
                  {
                    text: ` `,
                    preserveLeadingSpaces: true,
                    font: `PoppinsLM`,
                    fontSize: 8,
                  },
                  {
                    columns: [
                      {
                        text: `Name :`,
                        preserveLeadingSpaces: true,
                        font: `PoppinsLM`,
                        fontSize: 8,
                        bold: true,
                        width: `auto`,
                      },
                      {
                        text: ` ${props.patientInfo?.patientFullName}`,
                        preserveLeadingSpaces: true,
                        font: `KanitLM`,
                        fontSize: 8,
                        bold: true,
                        width: `auto`,
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: `HN : ${props.patientInfo?.hn}`,
                        font: `PoppinsLM`,
                        fontSize: 8,
                        bold: true,
                      },
                      {
                        text: `EN/AN : ${props.patientInfo?.patientEncounter}`,
                        font: `PoppinsLM`,
                        fontSize: 8,
                        bold: true,
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: `Birth date : ${props.patientInfo?.patientBirthdateEN}`,
                        font: `PoppinsLM`,
                        fontSize: 8,
                        bold: true,
                      },
                      {
                        text: `Age : ${props.patientInfo?.ageYear} years ${props.patientInfo?.ageMonth} months ${props.patientInfo?.ageDay} days`,
                        font: `PoppinsLM`,
                        fontSize: 8,
                        bold: true,
                      },
                    ],
                  },
                  {
                    text: `Gender : ${
                      props.patientInfo?.gender
                        ? props.patientInfo.gender === "F"
                          ? "Female"
                          : props.patientInfo.gender === "M"
                          ? "Male"
                          : props.patientInfo.gender
                        : "N/A"
                    }`,
                    font: `PoppinsLM`,
                    fontSize: 8,
                    bold: true,
                  },
                  {
                    text: ` `,
                    preserveLeadingSpaces: true,
                    font: `PoppinsLM`,
                    fontSize: 8,
                  },
                ],
                border: [false, true, true, true],
              },
            ],
          ],
        },
        layout: {
          paddingTop: function (i: any, node: any) {
            return 5;
          },
          paddingBottom: function (i: any, node: any) {
            return 5;
          },
          paddingLeft: function (i: any, node: any) {
            return 5;
          },
          paddingRight: function (i: any, node: any) {
            return 5;
          },
        },
        margin: [16, 16, 16, 0],
      };
    },
    footer: (currentPage: any, pageCount: any) => {
      if (props.footer) {
        return {
          columns: [
            {
              text: `Printed date : ${props.createdDate}`,
              alignment: `left`,
              font: `PoppinsLM`,
              fontSize: 8,
              width: `auto`,
            },
            {
              text: props.footer,
              alignment: `center`,
              font: `THSarabunNew`,
              fontSize: 12,
              width: `*`,
            },
            {
              text: `Page ` + currentPage.toString() + ` of ` + pageCount,
              alignment: `right`,
              font: `PoppinsLM`,
              fontSize: 8,
              width: `auto`,
            },
          ],
          margin: [16, 16, 16, 16],
        };
      } else {
        return {
          columns: [
            {
              text: `Printed date : ${props.createdDate}`,
              alignment: `left`,
              font: `PoppinsLM`,
              fontSize: 8,
            },
            {
              text: `Page ` + currentPage.toString() + ` of ` + pageCount,
              alignment: `right`,
              font: `PoppinsLM`,
              fontSize: 8,
            },
          ],
          margin: [16, 16, 16, 16],
        };
      }
    },
    images: {
      ...images,
      checked: FormSymbolImages.checked,
      unchecked: FormSymbolImages.unchecked,
      squarebox: FormSymbolImages.squarebox,
      checkmark: FormSymbolImages.checkmark,
      ...props.images,
    },
    styles: {
      ...(props.styles || {}),
    },
  };
};

export const AddHeaderSapiensInfoTH = async (doc: any, props: HeaderPrintListProps) => {
  const companyLogoForm = await CompanyLogoForm({
    type: 2,
    font: "KanitLM",
    height: LOGO_HEIGHT,
    form: FORM_NAME,
  });
  const { images } = companyLogoForm;

  return {
    ...doc,
    // * Logo height 100
    pageMargins: props.pageMargins ? props.pageMargins : DEFAULT_PAGE_MARGINS,
    header: function () {
      return {
        stack: [
          {
            columns: [
              // { image: 'logo', width: 150, height: 150 },
              { image: "logo", width: LOGO_WIDTH, height: LOGO_HEIGHT, margin: [10, 12, 0, 0] },
              { text: "", width: "*" },
              {
                table: {
                  body: [
                    [
                      {
                        stack: [
                          {
                            text: "โรงพยาบาลเซเปี้ยนซ์",
                            noWrap: true,
                            font: "KanitLM",
                            style: "hospitalName",
                          },
                          {
                            text: "498/7 ซอยรามคำแหง 39 (เทพลีลา 1)",
                            noWrap: true,
                            font: "KanitLM",
                            style: "hospitalInfo",
                          },
                          {
                            text: "แขวงวังทองหลาง เขตวังทองหลาง",
                            noWrap: true,
                            font: "KanitLM",
                            style: "hospitalInfo",
                          },
                          {
                            text: "กรุงเทพมหานคร 10310",
                            noWrap: true,
                            font: "KanitLM",
                            style: "hospitalInfo",
                          },
                        ],
                        border: [false, false, true, false],
                        alignment: "right",
                      },
                      {
                        stack: [
                          {
                            text: "Sapiens Pain Hospital",
                            noWrap: true,
                            font: "Poppins",
                            style: "hospitalName",
                          },
                          {
                            text: "498/7 Soi Ramkhamhaeng (Thepleela 1)",
                            noWrap: true,
                            font: "Poppins",
                            style: "hospitalInfo",
                          },
                          {
                            text: "Wang Thonglang, Wang Thonglang",
                            noWrap: true,
                            font: "Poppins",
                            style: "hospitalInfo",
                          },
                          {
                            text: "Bangkok 10310",
                            noWrap: true,
                            font: "Poppins",
                            style: "hospitalInfo",
                          },
                        ],
                        border: [true, false, false, false],
                        alignment: "right",
                      },
                    ],
                    [
                      {
                        text: "Tel. 02-111-3703",
                        font: "Poppins",
                        border: [false, false, false, false],
                        colSpan: 2,
                        alignment: "right",
                        style: "hospitalInfo",
                      },
                      {},
                    ],
                  ],
                },
                width: `auto`,
              },
            ],
            columnGap: 10,
            margin: [0, 16, 16, 0],
          },
        ].concat(props.header || ({ text: "" } as any)),
      };
    },
    footer: (currentPage: any, pageCount: any) => {
      return {
        columns: [
          {
            text: `หน้า ` + currentPage.toString() + ` จาก ` + pageCount,
            alignment: `left`,
            font: `KanitLM`,
            fontSize: 8,
          },
          {
            text: `วันที่พิมพ์/Printed date : ${props.createdDate}`,
            alignment: `right`,
            font: `KanitLM`,
            fontSize: 8,
          },
        ],
        margin: [16, 16, 16, 16],
      };
    },
    images: {
      ...images,
      checked: FormSymbolImages.checked,
      unchecked: FormSymbolImages.unchecked,
      squarebox: FormSymbolImages.squarebox,
      checkmark: FormSymbolImages.checkmark,
      ...props.images,
    },
    styles: {
      hospitalName: {
        fontSize: 7,
        bold: true,
      },
      hospitalInfo: {
        fontSize: 7,
      },
      header: {
        fontSize: 20,
        bold: true,
        margin: [0, 0, 0, 2],
      },
      ...(props.styles || {}),
    },
  };
};

export const AddHeaderSapiensPrintListStatic = async (doc: any, props: HeaderPrintListProps) => {
  const companyLogoForm = await CompanyLogoForm({
    type: 2,
    font: "KanitLM",
    height: LOGO_HEIGHT,
    form: FORM_NAME,
  });
  const { images } = companyLogoForm;

  const defaultHeaderMargins = [16, 16, 16, 0];

  const [leftHeaderMargin, topHeaderMargin, rightHeaderMargin, bottomHeaderMargin] =
    props.headerMargins || defaultHeaderMargins;

  const canvasWidth = 595 - (leftHeaderMargin + rightHeaderMargin);
  const canvasHeight = props.canvasHeight ?? 100;
  const extraCanvasHeight = props.extraCanvasHeight ?? 0;

  const defaultFreeLines = props.freeLine ?? 0;

  const getXFromLeft = (offsetLeft: number) => leftHeaderMargin + offsetLeft;

  const headerDef = {
    ...doc,
    // logo height 80 + marginHeader 16 = 96 (pageMargins must greater than Header + Header Bottom Margin)
    // margin: [left, top, right, bottom]
    pageMargins: props.pageMargins ? props.pageMargins : [38, canvasHeight, 38, 0],
    header: () => {
      return {
        stack: [
          {
            canvas: [
              {
                type: "rect",
                x: leftHeaderMargin,
                y: topHeaderMargin,
                w: canvasWidth,
                h: canvasHeight - topHeaderMargin + extraCanvasHeight,
                color: "white",
              },
            ],
            absolutePosition: { x: 0, y: 0 },
          },
          // Position
          {
            absolutePosition: { x: getXFromLeft(355), y: 24 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.patientName,
          },
          {
            absolutePosition: { x: getXFromLeft(510), y: 24 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.hn,
          },
          {
            absolutePosition: { x: getXFromLeft(380), y: 42 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.patient_birthdate,
          },
          {
            absolutePosition: { x: getXFromLeft(464), y: 42 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.patient_age,
          },
          {
            absolutePosition: { x: getXFromLeft(528), y: 42 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.patient_gender_name,
          },
          {
            absolutePosition: { x: getXFromLeft(380), y: 60 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.number || "-",
          },
          {
            absolutePosition: { x: getXFromLeft(500), y: 60 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.visit_date,
          },
          {
            absolutePosition: { x: getXFromLeft(400), y: 78 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.doctorName,
          },
          {
            absolutePosition: { x: getXFromLeft(535), y: 78 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.ipd_room || "-",
          },
          {
            absolutePosition: { x: getXFromLeft(360), y: 96 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: truncateTextSimple(props.detail?.allergies, 210, 8) || "-",
          },
          {
            table: {
              widths: ["55%", "45%"],
              body: [
                [
                  // Left Side (logo and form name)
                  {
                    stack: [
                      {
                        image: "logo",
                        width: LOGO_WIDTH,
                        height: LOGO_HEIGHT,
                        margin: [0, 0, 0, 24],
                      },
                      {
                        text: props.formName,
                        alignment: `center`,
                        font: `KanitLM`,
                        fontSize: 9,
                        bold: true,
                        margin: [0, -16, 0, 0],
                      },
                    ],
                    border: [true, true, false, true],
                  },
                  // Right Side (patient information)
                  {
                    stack: [
                      // * New Detail
                      {
                        text: " ",
                        preserveLeadingSpaces: true,
                        style: "fieldHeaderKey",
                      },
                      {
                        columns: [
                          {
                            text: "Name:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 140,
                                y1: 13,
                                y2: 13,
                              },
                            ],
                            width: "auto",
                          },
                          {
                            text: "HN:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 67,
                                y1: 13,
                                y2: 13,
                              },
                            ],
                            width: "auto",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            text: "Date of Birth:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 70,
                                y1: 13,
                                y2: 13,
                              },
                            ],
                            width: "auto",
                          },
                          {
                            text: "Age:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 30,
                                y1: 13,
                                y2: 13,
                              },
                            ],
                            width: "auto",
                          },
                          {
                            text: "Gender:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 52,
                                y1: 13,
                                y2: 13,
                              },
                            ],
                            width: "auto",
                          },
                        ],
                        width: "auto",
                      },
                      {
                        columns: [
                          {
                            text: "VN/ AN:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 80,
                                y1: 13,
                                y2: 13,
                              },
                            ],
                            width: "auto",
                          },
                          {
                            text: "Visit Date:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 96,
                                y1: 13,
                                y2: 13,
                              },
                            ],
                            width: "auto",
                          },
                        ],
                        width: "auto",
                      },
                      {
                        columns: [
                          {
                            text: "Attending Physician:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 113,
                                y1: 13,
                                y2: 13,
                              },
                            ],
                            width: "auto",
                          },
                          {
                            text: "Room:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 30,
                                y1: 13,
                                y2: 13,
                              },
                            ],
                            width: "auto",
                          },
                        ],
                        width: "auto",
                      },
                      {
                        columns: [
                          {
                            text: "Allergies:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 210,
                                y1: 13,
                                y2: 13,
                              },
                            ],
                            width: "auto",
                          },
                        ],
                        width: "auto",
                      },
                      ...new Array(defaultFreeLines).fill(0).map(() => ({
                        text: " ",
                        style: "gapSpace",
                      })),
                    ],
                    border: [false, true, true, true],
                  },
                ],
              ],
            },
            layout: {
              paddingTop: function (i: any, node: any) {
                return 5;
              },
              paddingBottom: function (i: any, node: any) {
                return 5;
              },
              paddingLeft: function (i: any, node: any) {
                return 5;
              },
              paddingRight: function (i: any, node: any) {
                return 5;
              },
            },
            margin: props.headerMargins ?? defaultHeaderMargins,
          },
        ],
      };
    },
    images: {
      ...images,
      checked: FormSymbolImages.checked,
      unchecked: FormSymbolImages.unchecked,
      squarebox: FormSymbolImages.squarebox,
      checkmark: FormSymbolImages.checkmark,
      ...props.images,
    },
    styles: {
      ...(props.styles || {}),
      fieldHeaderKey: {
        font: "KanitLM",
        fontSize: 8,
        lineHeight: 1.5,
        bold: true,
      },
      fieldHeaderValue: {
        font: "KanitLM",
        fontSize: 8,
        lineHeight: 1.5,
      },
      gapSpace: {
        font: "KanitLM",
        fontSize: 6,
        lineHeight: 1.5,
        preserveLeadingSpaces: true,
      },
    },
  };

  return calculateHeaderBasedMargins(headerDef);
};

export const AddHeaderSapiensPrintListStaticMini = async (
  doc: any,
  props: HeaderPrintListProps
) => {
  const companyLogoForm = await CompanyLogoForm({
    type: 2,
    font: "KanitLM",
    height: LOGO_HEIGHT,
    form: FORM_NAME,
  });
  const { images } = companyLogoForm;

  const defaultHeaderMargins = [16, 16, 16, 0];

  const [leftHeaderMargin, topHeaderMargin, rightHeaderMargin, bottomHeaderMargin] =
    props.headerMargins || defaultHeaderMargins;

  const canvasWidth = 595 - (leftHeaderMargin + rightHeaderMargin);
  const canvasHeight = props.canvasHeight ?? 100;
  const extraCanvasHeight = props.extraCanvasHeight ?? 0;

  const defaultFreeLines = props.freeLine ?? 0;

  const getXFromLeft = (offsetLeft: number) => leftHeaderMargin + offsetLeft;

  const headerDef = {
    ...doc,
    // logo height 80 + marginHeader 16 = 96 (pageMargins must greater than Header + Header Bottom Margin)
    // margin: [left, top, right, bottom]
    pageMargins: props.pageMargins ? props.pageMargins : [38, canvasHeight, 38, 0],
    header: () => {
      return {
        stack: [
          {
            canvas: [
              {
                type: "rect",
                x: leftHeaderMargin,
                y: topHeaderMargin,
                w: canvasWidth,
                // h: canvasHeight - (topHeaderMargin + 7),
                h: canvasHeight - topHeaderMargin + extraCanvasHeight,
                color: "white",
              },
            ],
            absolutePosition: { x: 0, y: 0 },
          },
          // Position
          {
            absolutePosition: { x: getXFromLeft(345), y: 6 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.patientName,
          },
          {
            absolutePosition: { x: getXFromLeft(500), y: 6 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.hn,
          },
          {
            absolutePosition: { x: getXFromLeft(370), y: 20 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.patient_birthdate,
          },
          {
            absolutePosition: { x: getXFromLeft(450), y: 20 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.patient_age,
          },
          {
            absolutePosition: { x: getXFromLeft(508), y: 20 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.patient_gender_name,
          },
          {
            absolutePosition: { x: getXFromLeft(380), y: 34 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.number || "-",
          },
          {
            absolutePosition: { x: getXFromLeft(490), y: 34 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.visit_date,
          },
          {
            absolutePosition: { x: getXFromLeft(380), y: 46 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.doctorName,
          },
          {
            absolutePosition: { x: getXFromLeft(508), y: 46 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: props.detail?.ipd_room || "-",
          },
          {
            absolutePosition: { x: getXFromLeft(350), y: 60 + topHeaderMargin },
            style: "fieldHeaderValue",
            text: truncateTextSimple(props.detail?.allergies, 250, 6) || "-",
          },
          {
            table: {
              widths: ["55%", "45%"],
              body: [
                [
                  // Left Side (logo and form name)
                  {
                    stack: [
                      {
                        image: "logo",
                        width: LOGO_WIDTH,
                        height: LOGO_HEIGHT,
                        margin: [0, 0, 0, 24],
                      },
                      {
                        text: props.formName,
                        alignment: `center`,
                        font: `KanitLM`,
                        fontSize: 9,
                        bold: true,
                        margin: [0, -16, 0, 0],
                      },
                    ],
                    border: [true, true, false, true],
                  },
                  // Right Side (patient information)
                  {
                    stack: [
                      // * New Detail
                      {
                        columns: [
                          {
                            text: "Name:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 140,
                                y1: 11,
                                y2: 11,
                              },
                            ],
                            width: "auto",
                          },
                          {
                            text: "HN:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 67,
                                y1: 11,
                                y2: 11,
                              },
                            ],
                            width: "auto",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            text: "Date of Birth:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 70,
                                y1: 11,
                                y2: 11,
                              },
                            ],
                            width: "auto",
                          },
                          {
                            text: "Age:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 30,
                                y1: 11,
                                y2: 11,
                              },
                            ],
                            width: "auto",
                          },
                          {
                            text: "Gender:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 65,
                                y1: 11,
                                y2: 11,
                              },
                            ],
                            width: "auto",
                          },
                        ],
                        width: "auto",
                      },
                      {
                        columns: [
                          {
                            text: "VN/ AN:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 80,
                                y1: 11,
                                y2: 11,
                              },
                            ],
                            width: "auto",
                          },
                          {
                            text: "Visit Date:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 103,
                                y1: 11,
                                y2: 11,
                              },
                            ],
                            width: "auto",
                          },
                        ],
                        width: "auto",
                      },
                      {
                        columns: [
                          {
                            text: "Attending Physician:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 113,
                                y1: 11,
                                y2: 11,
                              },
                            ],
                            width: "auto",
                          },
                          {
                            text: "Room:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 46,
                                y1: 11,
                                y2: 11,
                              },
                            ],
                            width: "auto",
                          },
                        ],
                        width: "auto",
                      },
                      {
                        columns: [
                          {
                            text: "Allergies:",
                            style: "fieldHeaderKey",
                            width: "auto",
                          },
                          {
                            canvas: [
                              {
                                type: "line",
                                lineWidth: 1,
                                x1: 0,
                                x2: 210,
                                y1: 11,
                                y2: 11,
                              },
                            ],
                            width: "auto",
                          },
                        ],
                        width: "auto",
                      },
                      ...new Array(defaultFreeLines).fill(0).map(() => ({
                        text: " ",
                        style: "gapSpace",
                      })),
                    ],
                    border: [false, true, true, true],
                  },
                ],
              ],
            },
            layout: {
              paddingTop: function (i: any, node: any) {
                return 5;
              },
              paddingBottom: function (i: any, node: any) {
                return 5;
              },
              paddingLeft: function (i: any, node: any) {
                return 5;
              },
              paddingRight: function (i: any, node: any) {
                return 5;
              },
            },
            margin: props.headerMargins ?? defaultHeaderMargins,
          },
        ],
      };
    },
    images: {
      ...images,
      checked: FormSymbolImages.checked,
      unchecked: FormSymbolImages.unchecked,
      squarebox: FormSymbolImages.squarebox,
      checkmark: FormSymbolImages.checkmark,
      ...props.images,
    },
    styles: {
      ...(props.styles || {}),
      fieldHeaderKey: {
        font: "KanitLM",
        fontSize: 6,
        lineHeight: 1.5,
        bold: true,
      },
      fieldHeaderValue: {
        font: "KanitLM",
        fontSize: 6,
        lineHeight: 1.5,
      },
      gapSpace: {
        font: "KanitLM",
        fontSize: 6,
        lineHeight: 1.5,
        preserveLeadingSpaces: true,
      },
    },
  };

  return calculateHeaderBasedMargins(headerDef);
};

export const AddHeaderSapiensPrintListStaticAddress = async (
  doc: any,
  props: HeaderPrintListProps
) => {
  const companyLogoForm = await CompanyLogoForm({
    type: 2,
    font: "KanitLM",
    height: LOGO_HEIGHT,
    form: FORM_NAME,
  });

  const { images, columns } = companyLogoForm;

  const defaultHeaderMargins = [16, 16, 16, 16];

  const [leftHeaderMargin, topHeaderMargin, rightHeaderMargin, bottomHeaderMargin] =
    props.headerMargins || defaultHeaderMargins;

  const canvasWidth = 595 - (leftHeaderMargin + rightHeaderMargin);
  const canvasHeight = props.canvasHeight ?? 100;

  const headerDef = {
    ...doc,
    ...companyLogoForm,
    header: () => {
      return {
        stack: [
          {
            canvas: [
              {
                type: "rect",
                x: leftHeaderMargin,
                y: topHeaderMargin,
                w: canvasWidth,
                h: canvasHeight - topHeaderMargin,
                color: "white",
              },
            ],
            absolutePosition: { x: 0, y: 0 },
          },
          {
            table: {
              widths: ["25%", "*", "auto"],
              body: [...(columns.length > 0 ? [[{ columns }, "", getAddressCompany()]] : [])],
            },
            layout: "noBorders",
          },
        ],
        margin: props.headerMargins ?? defaultHeaderMargins,
      };
    },
    images: {
      ...images,
    },
    styles: {
      ...(props.styles || {}),
    },
  };

  return calculateHeaderBasedMargins(headerDef);
};
